/**
 * Date formatting utilities with timezone support
 */

import logger from './logger';

/**
 * Format a date/datetime string in the user's timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} userTimezone - IANA timezone (e.g., 'America/New_York')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (dateString, userTimezone = 'America/New_York', options = {}) => {
  if (!dateString) return '';

  try {
    let date;

    if (typeof dateString === 'string') {
      // Check if it's a date-only string (YYYY-MM-DD format without time)
      // Date-only strings should be parsed as local dates, not UTC
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Has time component or different format, parse as usual
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }

    // Default options for date formatting
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };

    return new Intl.DateTimeFormat('en-US', {
      ...defaultOptions,
      timeZone: userTimezone
    }).format(date);
  } catch (error) {
    logger.error('Error formatting date', { error, dateString, userTimezone });
    return dateString.toString();
  }
};

/**
 * Format a datetime with time in the user's timezone
 * @param {string|Date} dateString - ISO date string or Date object
 * @param {string} userTimezone - IANA timezone
 * @param {object} options - Additional options
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeInTimezone = (dateString, userTimezone = 'America/New_York', options = {}) => {
  if (!dateString) return '';

  try {
    let date;

    if (typeof dateString === 'string') {
      // Check if it's a date-only string (YYYY-MM-DD format without time)
      // Date-only strings should be parsed as local dates, not UTC
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Has time component or different format, parse as usual
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options
    };

    return new Intl.DateTimeFormat('en-US', {
      ...defaultOptions,
      timeZone: userTimezone
    }).format(date);
  } catch (error) {
    logger.error('Error formatting datetime', { error, dateString, userTimezone });
    return dateString.toString();
  }
};

/**
 * Format a date for display (short format) - e.g., "Jan 15, 2024"
 */
export const formatDate = (dateString, userTimezone = 'America/New_York') => {
  return formatDateInTimezone(dateString, userTimezone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date for display (long format) - e.g., "January 15, 2024"
 */
export const formatDateLong = (dateString, userTimezone = 'America/New_York') => {
  return formatDateInTimezone(dateString, userTimezone, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format datetime with time - e.g., "Jan 15, 2024 at 3:30 PM"
 */
export const formatDateTime = (dateString, userTimezone = 'America/New_York') => {
  return formatDateTimeInTimezone(dateString, userTimezone);
};

/**
 * Format time only - e.g., "3:30 PM"
 */
export const formatTime = (dateString, userTimezone = 'America/New_York') => {
  if (!dateString) return '';

  try {
    let date;

    if (typeof dateString === 'string') {
      // Check if it's a date-only string (YYYY-MM-DD format without time)
      // Date-only strings should be parsed as local dates, not UTC
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Has time component or different format, parse as usual
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }

    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: userTimezone
    }).format(date);
  } catch (error) {
    logger.error('Error formatting time', { error, dateString, userTimezone });
    return dateString.toString();
  }
};

/**
 * Format relative time - e.g., "2 hours ago", "in 3 days"
 * Note: This doesn't account for timezone differences as relative time is timezone-agnostic
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return '';

  try {
    let date;

    if (typeof dateString === 'string') {
      // Check if it's a date-only string (YYYY-MM-DD format without time)
      // Date-only strings should be parsed as local dates, not UTC
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Has time component or different format, parse as usual
        date = new Date(dateString);
      }
    } else {
      date = dateString;
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateString);
    }
  } catch (error) {
    logger.error('Error formatting relative time', { error, dateString });
    return dateString.toString();
  }
};

/**
 * Hook to get user's timezone from context/storage
 * This should be used in components to get the current user's timezone
 */
export const useUserTimezone = () => {
  // Try to get from localStorage first (cached from user profile)
  const cachedTimezone = localStorage.getItem('userTimezone');
  return cachedTimezone || 'America/New_York'; // Default fallback
};

/**
 * Save user timezone to localStorage for quick access
 */
export const saveUserTimezone = (timezone) => {
  localStorage.setItem('userTimezone', timezone);
};

/**
 * Create a timezone-aware hook for formatting dates
 * Usage in components:
 * const formatUserDate = useDateFormatter();
 * formatUserDate(dateString) // Uses user's timezone automatically
 */
export const useDateFormatter = () => {
  const timezone = useUserTimezone();

  return {
    formatDate: (dateString) => formatDate(dateString, timezone),
    formatDateLong: (dateString) => formatDateLong(dateString, timezone),
    formatDateTime: (dateString) => formatDateTime(dateString, timezone),
    formatTime: (dateString) => formatTime(dateString, timezone),
    formatRelativeTime: formatRelativeTime,
    timezone
  };
};

/**
 * Convert a Date object to YYYY-MM-DD format in the user's local timezone
 * WITHOUT converting to UTC first (which is what toISOString() does)
 * This is critical for comparing dates in the calendar view
 *
 * @param {Date} date - Date object in local timezone
 * @param {string} userTimezone - IANA timezone (e.g., 'America/New_York')
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const toLocalDateString = (date, userTimezone = null) => {
  if (!date) return '';

  try {
    // If timezone is provided, format the date in that timezone
    if (userTimezone) {
      const year = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: userTimezone }).format(date);
      const month = new Intl.DateTimeFormat('en-US', { month: '2-digit', timeZone: userTimezone }).format(date);
      const day = new Intl.DateTimeFormat('en-US', { day: '2-digit', timeZone: userTimezone }).format(date);
      return `${year}-${month}-${day}`;
    }

    // Otherwise use the browser's local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    logger.error('Error converting date to local string', { error, date, userTimezone });
    // Fallback to ISO string split
    return date.toISOString().split('T')[0];
  }
};

/**
 * Get user's timezone from localStorage
 * @returns {string} User's timezone or default 'America/New_York'
 */
export const getUserTimezone = () => {
  return localStorage.getItem('userTimezone') || 'America/New_York';
};

/**
 * Detect browser's timezone using native API
 * @returns {string} Browser's IANA timezone or default 'America/New_York'
 */
export const detectBrowserTimezone = () => {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTimezone) {
      logger.info('Detected browser timezone:', browserTimezone);
      return browserTimezone;
    }
    logger.warn('Browser timezone detection returned empty, using default');
    return 'America/New_York';
  } catch (error) {
    logger.error('Failed to detect browser timezone', { error });
    return 'America/New_York';
  }
};
